import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button.js";
import { startSignin } from "@/lib/license-api.js";

const LICENSE_KEY_DRAFT = "agencylens:signup:licenseKey";

export function Login() {
  const [email, setEmail] = useState("");
  const [licenseKey, setLicenseKey] = useState("");
  const [submitted, setSubmitted] = useState<{
    sent: boolean;
    devMagicLink?: string;
    warning?: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ error: string; hint?: string } | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const lk = licenseKey.trim() || undefined;
      // Stash the license key locally so /login/verify can pass it through
      // for a same-browser signup. Cross-browser signups will prompt for it
      // again on the verify page.
      if (lk) sessionStorage.setItem(LICENSE_KEY_DRAFT, lk);
      const res = await startSignin(email.trim(), lk);
      setSubmitted({
        sent: res.sent,
        devMagicLink: res.devMagicLink,
        warning: res.warning,
      });
    } catch (e) {
      const ee = e as Error & { error?: string; hint?: string };
      setErr({ error: ee.error ?? ee.message, hint: ee.hint });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-12">
      <div className="mb-8 text-center">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-900">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-brand-700 text-white text-sm font-semibold">
            AL
          </span>
          <span className="text-lg font-semibold tracking-tight">AgencyLens</span>
        </Link>
      </div>

      {submitted ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-sm text-emerald-900">
          {submitted.sent ? (
            <>
              <h2 className="mb-2 text-base font-semibold">Check your email.</h2>
              <p>
                We sent a sign-in link to <strong>{email}</strong>. The link
                works once and expires in 15 minutes.
              </p>
              <p className="mt-3 text-xs text-emerald-700">
                Didn't see it? Check your spam folder, then try{" "}
                <button
                  onClick={() => setSubmitted(null)}
                  className="underline"
                  type="button"
                >
                  resending
                </button>
                .
              </p>
            </>
          ) : (
            <>
              <h2 className="mb-2 text-base font-semibold">Dev mode — email not configured</h2>
              <p>
                {submitted.warning ??
                  "Email delivery is not provisioned yet. Click the link below to continue:"}
              </p>
              {submitted.devMagicLink && (
                <p className="mt-3 break-all text-xs">
                  <a
                    href={submitted.devMagicLink}
                    className="text-brand-700 underline"
                  >
                    {submitted.devMagicLink}
                  </a>
                </p>
              )}
            </>
          )}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4 rounded-lg border border-slate-200 bg-white p-6">
          <h1 className="text-xl font-semibold tracking-tight">Sign in</h1>
          <p className="text-sm text-slate-600">
            We'll email you a one-time sign-in link. No password to remember.
          </p>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-slate-700">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-md border border-slate-200 px-3 py-2 text-sm"
              placeholder="you@example.com"
              autoFocus
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-slate-700">
              AppSumo license key{" "}
              <span className="text-xs font-normal text-slate-500">
                (required on first sign-up only)
              </span>
            </span>
            <input
              type="text"
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value)}
              className="rounded-md border border-slate-200 px-3 py-2 text-sm font-mono"
              placeholder="AS-XXXXXXXX-XXXXX"
              autoComplete="off"
            />
          </label>

          {err && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
              <div className="font-medium">{prettyError(err.error)}</div>
              {err.hint && <div className="mt-1 text-xs text-red-800">{err.hint}</div>}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? "Sending sign-in link…" : "Send sign-in link"}
          </Button>

          <p className="text-xs text-slate-500">
            Don't have a license yet? AgencyLens is coming soon to{" "}
            <a
              href="https://agencylens.app/"
              className="underline"
              target="_blank"
              rel="noreferrer"
            >
              AppSumo Marketplace
            </a>
            .
          </p>
        </form>
      )}
    </div>
  );
}

function prettyError(code: string): string {
  switch (code) {
    case "invalid_email":
      return "That doesn't look like a valid email address.";
    case "invalid_license_key":
      return "We couldn't find that license key.";
    case "license_already_claimed":
      return "That license is already registered to a different email address.";
    case "license_key_required":
      return "First-time sign-up needs your AppSumo license key.";
    case "origin_not_allowed":
      return "This origin isn't allowed to call the license worker.";
    case "session_secret_not_configured":
      return "Auth is not provisioned yet. Operator must set SESSION_SECRET on the license worker.";
    default:
      return code.replace(/_/g, " ");
  }
}
